import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Checkbox, Select, TextInput } from '@mantine/core';
import ResponsiveModal from '../../common/ResponsiveModal';
import AppStack from '../../common/AppStack';
import { Context as AdminContext } from '../../../providers/AdminContextProvider';
import AppText from '../../common/AppText';
import { EXTERNAL_CONNECTION_CONFIGS } from '../external/config/externalConnectionConfigs';
import { triggerNotification } from '../../../helpers/notification';
import { getOrderSummaryInfo } from '../../../helpers/storeHelper';

const AdminOrderUpdateExternalIdModal = ({
  isOpen,
  onClose,
  onSuccess,
  ecomOrder
}) => {
  const [formState, setFormState] = useState({
    externalId: '',
    fkEcomExternalConnectionType: null,
    showConfirm: false,
    loading: false
  });
  const { updateEcomOrderExternalConnectionId } = useContext(AdminContext);

  const config = EXTERNAL_CONNECTION_CONFIGS.find(
    (c) =>
      c.pkEcomExternalConnectionType.toString() ===
      formState.fkEcomExternalConnectionType
  );
  const { externalOrderInfo } = ecomOrder ? getOrderSummaryInfo(ecomOrder) : {};

  useEffect(() => {
    if (isOpen) {
      setFormState({
        externalId: '',
        fkEcomExternalConnectionType: null,
        showConfirm: false,
        loading: false
      });
    }
  }, [isOpen]);

  return (
    <ResponsiveModal
      formSectionProps={
        formState.showConfirm
          ? {
              onCancel: onClose,
              isLoading: formState.loading,
              submitTitle: 'Yes, update ID',
              onSubmit: () => {
                setFormState({ ...formState.loading });

                updateEcomOrderExternalConnectionId(
                  ecomOrder.pkEcomOrder,
                  {
                    externalId: formState.externalId,
                    fkEcomExternalConnectionType:
                      formState.fkEcomExternalConnectionType,
                    ecomExternalConnectionId: null
                  },
                  () => {
                    triggerNotification('Order Id updated', 'success');
                    setFormState({
                      ...formState,
                      loading: false
                    });
                    if (onSuccess) {
                      onSuccess();
                    }
                  },
                  (e) => {
                    triggerNotification(e);
                    setFormState({ ...formState, loading: false });
                  }
                );
              }
            }
          : {
              onCancel: onClose,
              isLoading: formState.loading,
              onSubmit: () => {
                setFormState({ ...formState, showConfirm: true });
              },
              isSubmitDisabled:
                !formState.externalId &&
                !formState.fkEcomExternalConnectionType,
              submitTitle: 'Continue'
            }
      }
      isOpen={isOpen}
      onClose={onClose}
      title="Update external order ID"
    >
      {formState.showConfirm ? (
        <AppStack style={{ gap: 16 }}>
          <AppText>
            Are you sure you want to change the external ID for {config?.title}{' '}
            to {formState.externalId}?
          </AppText>

          <AppText style={{ fontSize: 14, fontWeight: 500 }}>
            This action cannot be undone.
          </AppText>

          <Checkbox label="I confirm this action" required />
        </AppStack>
      ) : (
        <AppStack style={{ gap: 16 }}>
          <Select
            clearable
            data={
              externalOrderInfo?.map((i) => ({
                label: i.title,
                description: null,
                value: i.fkEcomExternalConnectionType.toString()
              })) || []
            }
            label="External Connection"
            onChange={(value) =>
              setFormState({
                ...formState,
                fkEcomExternalConnectionType: value
              })
            }
            required
            searchable
            value={formState.fkEcomExternalConnectionType}
          />

          <TextInput
            label="External ID"
            onChange={(e) =>
              setFormState({ ...formState, externalId: e.currentTarget.value })
            }
            required
            value={formState.externalId}
          />
        </AppStack>
      )}
    </ResponsiveModal>
  );
};

AdminOrderUpdateExternalIdModal.propTypes = {
  ecomOrder: PropTypes.object,
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  onSuccess: PropTypes.func
};

export default AdminOrderUpdateExternalIdModal;
